<template>
  <button
    :class="[
      'q-button',
      `q-button--${size}`,
      `q-button--${variant}`,
      {
        'q-button--disabled': disabled || loading,
        'q-button--hover': hover,
        'q-button--active': active,
        'q-button--full-width': fullWidth,
        'q-button--loading': loading,
      },
    ]"
    :disabled="disabled || loading"
    v-bind="$attrs"
    @click="$emit('click', $event)"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
  >
    <div class="q-button__content">
      <div
        v-if="loading"
        class="q-button__spinner"
      >
        <svg
          class="q-button__spinner-icon"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            vector-effect="non-scaling-stroke"
            stroke-width="2"
            stroke-linecap="round"
            stroke-dasharray="31.416"
            stroke-dashoffset="31.416"
          />
        </svg>
      </div>

      <div
        v-else-if="$slots.icon"
        class="q-button__icon"
      >
        <slot name="icon" />
      </div>

      <!-- v-if="!loading" -->
      <span 
        v-if="!loading && $slots.default" 
        class="q-button__text"
      >
        <slot />
      </span>
    </div>

    <div
      v-if="badge"
      class="q-button__badge"
    >
      {{ badge }}
    </div>
  </button>
</template>

<script setup>
defineProps({
  size: {
    type: String,
    default: 'm',
    validator: (value) => ['l', 'm', 's'].includes(value),
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'extra', 'tertiary', 'invert', 'outline', 'danger'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  badge: {
    type: [String, Number],
    default: null,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['click', 'mouseenter', 'mouseleave']);
</script>

<style lang="scss" scoped>
/* ===========================
   ⚡ Must be first
=========================== */
@use "sass:map";

/* ===========================
   🌈 Base styles
=========================== */
.q-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  outline: none;
  overflow: visible;
  min-width: fit-content;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }

  &:not(:disabled):active {
    transform: translateY(0);
  }
}

.q-button--full-width { width: 100%; }

.q-button__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.q-button__text { white-space: nowrap; }

.q-button__badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.08);
  z-index: 1;
  pointer-events: none;
}

.q-button__spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.q-button__spinner-icon { animation: spin 1s linear infinite; }

/* Ensure icon wrapper centers its SVG and avoids baseline offset */
.q-button__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.q-button__icon > svg {
  display: block;
  width: 100%;
  height: 100%;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ===========================
   📏 Size variants
=========================== */
$button-sizes: (
  l: (padding: 0 24px, height: 52px, font-size: 16px, radius: 12px, icon: 28px, spinner-stroke: 2.5px, spinner-speed: 0.8s),
  m: (padding: 0 20px, height: 44px, font-size: 16px, radius: 10px, icon: 24px, spinner-stroke: 2px, spinner-speed: 1s),
  s: (padding: 0 16px, height: 36px, font-size: 14px, radius: 8px, icon: 20px, spinner-stroke: 1.5px, spinner-speed: 1.2s)
);

@mixin button-size($size) {
  $props: map.get($button-sizes, $size);
  padding: map.get($props, padding);
  height: map.get($props, height);
  min-height: map.get($props, height);
  font-size: map.get($props, font-size);
  border-radius: map.get($props, radius);

  .q-button__icon,
  .q-button__spinner-icon {
    width: map.get($props, icon);
    height: map.get($props, icon);
  }

  .q-button__spinner-icon {
    animation-duration: map.get($props, spinner-speed, 1s);
  }

  .q-button__spinner-icon circle {
    stroke-width: map.get($props, spinner-stroke, 2px);
  }

  @if $size == s {
    .q-button__content { gap: 4px; }
  }
}

$button-variants: (
  primary: (
    background: #6ca1f8,
    color: #FFFFFF,
    hover-bg: #3b82f6,
    active-bg: #2273f6,
    badge-bg: #FFFFFF,
    badge-color: #2273f6,
    spinner-color: #FFFFFF
  ),
  secondary: (
    background: #FFFFFF,
    color: rgba(14, 14, 34, 0.8),
    hover-color: rgba(14, 14, 34, 0.9),
    active-color: rgba(14, 14, 34, 0.95),
    badge-bg: #3b82f6,
    badge-color: #FFFFFF,
    spinner-color: #3b82f6
  ),
  extra: (
    background: #FFFFFF,
    color: #6ca1f8,
    hover-color: #3b82f6,
    active-color: #2273f6,
    badge-bg: #3b82f6,
    badge-color: #FFFFFF,
    spinner-color: #3b82f6
  ),
  tertiary: (
    background: rgba(14, 14, 34, 0),
    color: rgba(14, 14, 34, 0.8),
    hover-bg: rgba(14, 14, 34, 0.06),
    active-bg: rgba(14, 14, 34, 0.12),
    badge-bg: #3b82f6,
    badge-color: #FFFFFF,
    spinner-color: #3b82f6
  ),
  invert: (
    background: rgba(255, 255, 255, 0),
    color: rgba(255, 255, 255, 0.8),
    border: 1.5px solid rgba(255, 255, 255, 0.8),
    hover-bg: rgba(255, 255, 255, 0.06),
    hover-color: rgba(255, 255, 255, 0.9),
    active-bg: rgba(255, 255, 255, 0.12),
    active-color: #FFFFFF,
    badge-bg: #FFFFFF,
    badge-color: #6ca1f8,
    spinner-color: #FFFFFF
  ),
  outline: (
    background: transparent,
    color: #6ca1f8,
    border: 1px solid #6ca1f8,
    hover-bg: rgba(108, 161, 248, 0.08),
    hover-color: #3b82f6,
    active-bg: rgba(108, 161, 248, 0.16),
    active-color: #2273f6,
    spinner-color: #6ca1f8
  )
);

@each $size, $props in $button-sizes {
  .q-button--#{$size} { @include button-size($size); }
}

/* duplicate color variants block removed to avoid redefinition */
$button-variants: (
  primary: (
    background: #6ca1f8,
    color: #FFFFFF,
    hover-bg: #3b82f6,
    active-bg: #2273f6,
    badge-bg: #FFFFFF,
    badge-color: #2273f6
  ),
  secondary: (
    background: #FFFFFF,
    color: rgba(14, 14, 34, 0.8),
    hover-color: rgba(14, 14, 34, 0.9),
    active-color: rgba(14, 14, 34, 0.95),
    badge-bg: #3b82f6,
    badge-color: #FFFFFF
  ),
  extra: (
    background: #FFFFFF,
    color: #6ca1f8,
    hover-color: #3b82f6,
    active-color: #2273f6,
    badge-bg: #3b82f6,
    badge-color: #FFFFFF
  ),
  tertiary: (
    background: rgba(14, 14, 34, 0),
    color: rgba(14, 14, 34, 0.8),
    hover-bg: rgba(14, 14, 34, 0.06),
    active-bg: rgba(14, 14, 34, 0.12),
    badge-bg: #3b82f6,
    badge-color: #FFFFFF
  ),
  invert: (
    background: rgba(255, 255, 255, 0),
    color: rgba(255, 255, 255, 0.8),
    border: 1.5px solid rgba(255, 255, 255, 0.8),
    hover-bg: rgba(255, 255, 255, 0.06),
    hover-color: rgba(255, 255, 255, 0.9),
    active-bg: rgba(255, 255, 255, 0.12),
    active-color: #FFFFFF,
    badge-bg: #FFFFFF,
    badge-color: #6ca1f8
  ),
  outline: (
    background: transparent,
    color: #6ca1f8,
    border: 1px solid #6ca1f8,
    hover-bg: rgba(108, 161, 248, 0.08),
    hover-color: #3b82f6,
    active-bg: rgba(108, 161, 248, 0.16),
    active-color: #2273f6,
    badge-bg: #3b82f6,
    badge-color: #FFFFFF,
    spinner-color: #6ca1f8
  ),
  danger: (
    background: transparent,
    color: #fe4a49,
    border: 1px solid #fe4a49,
    hover-bg: rgba(254, 74, 73, 0.08),
    hover-color: #d96060,
    active-bg: rgba(254, 74, 73, 0.16),
    active-color: #d96060,
    badge-bg: #fe4a49,
    badge-color: #FFFFFF,
    spinner-color: #fe4a49
  )
);

@mixin button-variant($name) {
  $v: map.get($button-variants, $name);
  $background: map.get($v, background);
  $color: map.get($v, color);
  $border: map.get($v, border);

  background-color: $background;
  color: $color;
  border: $border;

  &:not(:disabled):hover {
    background-color: map.get($v, hover-bg) or $background;
    color: map.get($v, hover-color) or $color;
    border-color: map.get($v, hover-border) or $border;
  }

  &:not(:disabled):active {
    background-color: map.get($v, active-bg) or $background;
    color: map.get($v, active-color) or $color;
    border-color: map.get($v, active-border) or $border;
  }
}

@each $name, $v in $button-variants {
  .q-button--#{$name} {
    @include button-variant($name);

    .q-button__badge {
      background-color: map.get($v, badge-bg);
      color: map.get($v, badge-color);
    }

    .q-button__spinner-icon {
      color: map.get($v, spinner-color, map.get($v, color));
    }
  }
}
</style>
